import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { AppPillSwitch } from '../../components/ui';
import PersonPills from '../../components/common/PersonPills';
import { notify } from '../../components/common/AppToast';
import kkpSommaireService from '../../services/kkpSommaireService';
import useAppConfirm from '../../hooks/useAppConfirm';
import '../../styles/kkp-sommaire-page.scss';
import '../../styles/kkp-planning-view-mode.scss';

const MOIS_LABELS = {
  1: 'Janvier', 2: 'Février', 3: 'Mars', 4: 'Avril', 5: 'Mai', 6: 'Juin',
  7: 'Juillet', 8: 'Août', 9: 'Septembre', 10: 'Octobre', 11: 'Novembre', 12: 'Décembre',
};

const SIDEBAR_LIST_LIMIT = 5;

const currentPeriod = () => {
  const now = new Date();
  return { annee: now.getFullYear(), mois: now.getMonth() + 1 };
};

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const KkpSommairePage = () => {
  const { ask, modal } = useAppConfirm();
  const period = useMemo(() => currentPeriod(), []);
  const [pageTab, setPageTab] = useState('sommaires');
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [carnets, setCarnets] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCarnetId, setSelectedCarnetId] = useState('');
  const [manageCarnetId, setManageCarnetId] = useState(null);
  const [extraEmails, setExtraEmails] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [newCarnetName, setNewCarnetName] = useState('');
  const [carnetNameEdit, setCarnetNameEdit] = useState('');
  const [newContact, setNewContact] = useState({ name: '', email: '' });
  const [createForm, setCreateForm] = useState({
    annee: period.annee,
    mois: period.mois,
  });
  const [listExpanded, setListExpanded] = useState(false);
  const [linkModal, setLinkModal] = useState(null);
  const texteBrutRef = useRef(null);
  const linkTextRef = useRef(null);
  const linkUrlRef = useRef(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await kkpSommaireService.list();
      setItems(data?.items ?? []);
      if (Array.isArray(data?.meta?.users)) {
        setUsers(data.meta.users);
      }
    } catch (err) {
      notify.error(err.message || 'Impossible de charger les sommaires');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCarnets = useCallback(async () => {
    try {
      const data = await kkpSommaireService.listCarnets();
      const list = data.carnets ?? [];
      setCarnets(list);
      if (Array.isArray(data.users)) {
        setUsers(data.users);
      }
      setManageCarnetId((prev) => {
        if (prev && list.some((c) => c.id === prev)) return prev;
        return list[0]?.id ?? null;
      });
      setSelectedCarnetId((prev) => {
        if (prev && list.some((c) => c.id === Number(prev) || c.id === prev)) return String(prev);
        return list[0] ? String(list[0].id) : '';
      });
    } catch (err) {
      notify.error(err.message || 'Impossible de charger les carnets');
    }
  }, []);

  useEffect(() => {
    loadList();
    loadCarnets();
  }, [loadList, loadCarnets]);

  const manageCarnet = useMemo(
    () => carnets.find((c) => c.id === manageCarnetId) || null,
    [carnets, manageCarnetId],
  );

  useEffect(() => {
    setCarnetNameEdit(manageCarnet?.name || '');
  }, [manageCarnet?.id, manageCarnet?.name]);

  const userPillItems = useMemo(
    () => users.map((u) => ({
      id: u.id,
      label: u.fullName || u.email,
      email: u.email || null,
    })),
    [users],
  );

  const selectedUserIds = useMemo(() => {
    const emails = new Set(
      (manageCarnet?.contacts || []).map((c) => normalizeEmail(c.email)).filter(Boolean),
    );
    return users
      .filter((u) => emails.has(normalizeEmail(u.email)))
      .map((u) => u.id);
  }, [manageCarnet, users]);

  const openSommaire = useCallback(async (id) => {
    try {
      const sommaire = await kkpSommaireService.get(id);
      setSelectedId(id);
      setDraft(sommaire);
      setExtraEmails('');
    } catch (err) {
      notify.error(err.message || 'Impossible d’ouvrir le sommaire');
    }
  }, []);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const sommaire = await kkpSommaireService.create({
        annee: Number(createForm.annee),
        mois: Number(createForm.mois),
      });
      await loadList();
      setSelectedId(sommaire.id);
      setDraft(sommaire);
      notify.success('Sommaire créé');
    } catch (err) {
      notify.error(err.message || 'Création impossible');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!draft?.id) return;
    setSaving(true);
    try {
      const sommaire = await kkpSommaireService.update(draft.id, {
        titre: draft.titre,
        intro_mail: draft.intro_mail,
        texte_brut: draft.texte_brut ?? '',
        annee: draft.annee,
        mois: draft.mois,
      });
      setDraft(sommaire);
      await loadList();
      notify.success('Enregistré');
    } catch (err) {
      notify.error(err.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft?.id) return;
    const ok = await ask({
      title: 'Supprimer ce sommaire ?',
      message: 'Le sommaire sera définitivement retiré.',
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await kkpSommaireService.remove(draft.id);
      setDraft(null);
      setSelectedId(null);
      await loadList();
      notify.success('Sommaire supprimé');
    } catch (err) {
      notify.error(err.message || 'Suppression impossible');
    }
  };

  const handleCreateCarnet = async () => {
    const name = newCarnetName.trim();
    if (!name) {
      notify.warning('Indiquez un nom de carnet');
      return;
    }
    try {
      const carnet = await kkpSommaireService.createCarnet({ name });
      setNewCarnetName('');
      await loadCarnets();
      setManageCarnetId(carnet.id);
      notify.success('Carnet créé');
    } catch (err) {
      notify.error(err.message || 'Création du carnet impossible');
    }
  };

  const handleRenameCarnet = async () => {
    if (!manageCarnetId) return;
    const name = carnetNameEdit.trim();
    if (!name) {
      notify.warning('Indiquez un nom de carnet');
      return;
    }
    try {
      await kkpSommaireService.updateCarnet(manageCarnetId, { name });
      await loadCarnets();
      notify.success('Carnet renommé');
    } catch (err) {
      notify.error(err.message || 'Renommage impossible');
    }
  };

  const handleDeleteCarnet = async (id) => {
    const ok = await ask({
      title: 'Supprimer ce carnet ?',
      message: 'Le carnet et tous ses contacts seront supprimés.',
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await kkpSommaireService.removeCarnet(id);
      await loadCarnets();
      notify.success('Carnet supprimé');
    } catch (err) {
      notify.error(err.message || 'Suppression impossible');
    }
  };

  const handleToggleUser = async (userId) => {
    if (!manageCarnetId || contactBusy) return;
    const user = users.find((u) => Number(u.id) === Number(userId));
    if (!user?.email) {
      notify.warning('Ce compte n’a pas d’e-mail');
      return;
    }
    const email = normalizeEmail(user.email);
    const existing = (manageCarnet?.contacts || []).find(
      (c) => normalizeEmail(c.email) === email,
    );

    setContactBusy(true);
    try {
      if (existing) {
        await kkpSommaireService.removeContact(existing.id);
      } else {
        await kkpSommaireService.createContact({
          carnet_id: manageCarnetId,
          name: user.fullName || user.email,
          email: user.email,
        });
      }
      await loadCarnets();
    } catch (err) {
      notify.error(err.message || 'Mise à jour impossible');
    } finally {
      setContactBusy(false);
    }
  };

  const handleAddContact = async () => {
    if (!manageCarnetId) {
      notify.warning('Créez ou sélectionnez un carnet d’abord');
      return;
    }
    setContactBusy(true);
    try {
      await kkpSommaireService.createContact({
        carnet_id: manageCarnetId,
        name: newContact.name,
        email: newContact.email,
      });
      setNewContact({ name: '', email: '' });
      await loadCarnets();
      notify.success('Contact ajouté');
    } catch (err) {
      notify.error(err.message || 'Ajout impossible');
    } finally {
      setContactBusy(false);
    }
  };

  const handleRemoveContact = async (id) => {
    setContactBusy(true);
    try {
      await kkpSommaireService.removeContact(id);
      await loadCarnets();
    } catch (err) {
      notify.error(err.message || 'Suppression impossible');
    } finally {
      setContactBusy(false);
    }
  };

  const visibleItems = useMemo(() => {
    if (listExpanded || items.length <= SIDEBAR_LIST_LIMIT) return items;
    return items.slice(0, SIDEBAR_LIST_LIMIT);
  }, [items, listExpanded]);

  const openLinkModal = useCallback(() => {
    const el = texteBrutRef.current;
    if (!el || !draft) return;

    const value = draft.texte_brut || '';
    let start = el.selectionStart ?? 0;
    let end = el.selectionEnd ?? 0;
    let selected = value.slice(start, end);
    let label = selected;
    let url = 'https://';

    // Si la sélection est déjà un lien markdown [texte](url), on l’édite
    const mdMatch = selected.match(/^\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)$/i);
    if (mdMatch) {
      label = mdMatch[1];
      url = mdMatch[2];
    } else if (/^https?:\/\//i.test(selected.trim()) || /^www\./i.test(selected.trim())) {
      url = selected.trim();
      label = selected.trim();
    } else {
      // Sélection à l’intérieur d’un lien markdown existant
      const before = value.slice(0, start);
      const after = value.slice(end);
      const openIdx = before.lastIndexOf('[');
      const closeIdx = after.indexOf(')');
      if (openIdx !== -1 && closeIdx !== -1) {
        const candidate = value.slice(openIdx, end + closeIdx + 1);
        const inner = candidate.match(/^\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)$/i);
        if (inner) {
          start = openIdx;
          end = end + closeIdx + 1;
          label = inner[1];
          url = inner[2];
        }
      }
    }

    setLinkModal({
      start,
      end,
      text: label,
      url,
      editing: Boolean(mdMatch) || (label !== selected && url !== 'https://'),
    });
  }, [draft]);

  const closeLinkModal = () => setLinkModal(null);

  useEffect(() => {
    if (!linkModal) return undefined;
    const focusEl = linkModal.text?.trim() ? linkUrlRef.current : linkTextRef.current;
    const t = window.setTimeout(() => {
      focusEl?.focus();
      focusEl?.select?.();
    }, 30);
    return () => window.clearTimeout(t);
  }, [linkModal]);

  const confirmLinkModal = (e) => {
    e?.preventDefault?.();
    if (!linkModal || !draft) return;

    let href = String(linkModal.url || '').trim();
    const text = String(linkModal.text || '').trim();
    if (!href) {
      notify.warning('Indiquez une adresse web');
      return;
    }
    if (/^www\./i.test(href)) href = `https://${href}`;
    if (!/^https?:\/\//i.test(href)) {
      notify.warning('Le lien doit commencer par https:// ou www.');
      return;
    }

    const label = text || href;
    const insertion = `[${label}](${href})`;
    const value = draft.texte_brut || '';
    const next = `${value.slice(0, linkModal.start)}${insertion}${value.slice(linkModal.end)}`;
    setDraft((p) => ({ ...p, texte_brut: next }));
    setLinkModal(null);

    requestAnimationFrame(() => {
      const el = texteBrutRef.current;
      if (!el) return;
      el.focus();
      const caret = linkModal.start + insertion.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const handleValidateSend = async () => {
    if (!draft?.id) return;
    if (!(draft.texte_brut || '').trim()) {
      notify.warning('Collez le sommaire avant d’envoyer');
      return;
    }

    const recipientEmails = extraEmails
      .split(/[,\s;]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (!selectedCarnetId && recipientEmails.length === 0) {
      notify.warning('Choisissez un carnet ou ajoutez au moins un e-mail');
      return;
    }

    const ok = await ask({
      title: 'Valider ce sommaire ?',
      message: '• Mail envoyé tel que collé\n• Placement CDF aux titres de rubriques reconnus',
      confirmLabel: 'Valider et envoyer',
      confirmVariant: 'primary',
    });
    if (!ok) {
      return;
    }

    setSending(true);
    try {
      await kkpSommaireService.update(draft.id, {
        titre: draft.titre,
        intro_mail: draft.intro_mail,
        texte_brut: draft.texte_brut ?? '',
        annee: draft.annee,
        mois: draft.mois,
      });
      const result = await kkpSommaireService.validateAndSend(draft.id, {
        carnet_id: selectedCarnetId ? Number(selectedCarnetId) : null,
        recipient_emails: recipientEmails,
      });
      setDraft(result.sommaire);
      await loadList();
      const placed = (result.cdf_placements || []).length;
      notify.success(
        placed > 0
          ? `Envoyé. ${placed} rubrique(s) placée(s) dans le CDF.`
          : 'Envoyé (aucune rubrique reconnue à placer dans le CDF).',
      );
    } catch (err) {
      notify.error(err.message || 'Validation / envoi impossible');
    } finally {
      setSending(false);
    }
  };

  const pageTabs = useMemo(() => [
    { value: 'sommaires', label: 'Sommaires', icon: 'mdi:newspaper-variant-outline', count: items.length },
    { value: 'carnets', label: 'Carnets', icon: 'mdi:card-account-mail-outline', count: carnets.length },
  ], [items.length, carnets.length]);

  return (
    <MasterLayout>
      <Breadcrumb
        title="Sommaire MAG"
        subtitle="Rédigez les sommaires et gérez les carnets d’envoi."
      />
      <div className="kkp-planning-page kkp-sommaire-page d-flex flex-column gap-3">
        <div className="kkp-planning-view-toolbar justify-content-start">
          <AppPillSwitch
            options={pageTabs}
            value={pageTab}
            onChange={setPageTab}
            ariaLabel="Onglets Sommaire MAG"
          />
        </div>

        {pageTab === 'sommaires' && (
          <div className="kkp-sommaire-layout">
            <aside className="kkp-sommaire-sidebar-col">
              <div className="kkp-sommaire-sidebar">
                <div className="kkp-sommaire-sidebar__head">
                  <span>Sommaires</span>
                  <span className="kkp-sommaire-sidebar__count">{items.length}</span>
                </div>

                <div className="kkp-sommaire-row" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: '0.55rem' }}>
                  <div className="kkp-sommaire-field" style={{ marginBottom: 0 }}>
                    <label htmlFor="new-mois">Mois</label>
                    <select
                      id="new-mois"
                      value={createForm.mois}
                      onChange={(e) => setCreateForm((p) => ({ ...p, mois: Number(e.target.value) }))}
                    >
                      {Object.entries(MOIS_LABELS).map(([num, label]) => (
                        <option key={num} value={num}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="kkp-sommaire-field" style={{ marginBottom: 0 }}>
                    <label htmlFor="new-annee">Année</label>
                    <input
                      id="new-annee"
                      type="number"
                      value={createForm.annee}
                      onChange={(e) => setCreateForm((p) => ({ ...p, annee: Number(e.target.value) }))}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary w-100 kkp-sommaire-new-btn"
                  onClick={handleCreate}
                  disabled={saving}
                >
                  <Icon icon="mdi:plus" className="me-1" />
                  Nouveau
                </button>

                {loading ? (
                  <div className="kkp-sommaire-empty">Chargement…</div>
                ) : items.length === 0 ? (
                  <div className="kkp-sommaire-empty">Aucun sommaire.</div>
                ) : (
                  <>
                    <div className="kkp-sommaire-list">
                      {visibleItems.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`kkp-sommaire-card kkp-sommaire-card--compact${selectedId === item.id ? ' kkp-sommaire-card--active' : ''}`}
                          onClick={() => openSommaire(item.id)}
                        >
                          <span className="kkp-sommaire-card__title">{item.titre}</span>
                          <span className="kkp-sommaire-card__meta">
                            <span className={`kkp-sommaire-badge kkp-sommaire-badge--${item.statut}`}>
                              {item.statut === 'validated' ? 'Validé' : 'Brouillon'}
                            </span>
                            <span>{item.envois_count || 0} envoi(s)</span>
                          </span>
                        </button>
                      ))}
                    </div>
                    {items.length > SIDEBAR_LIST_LIMIT && (
                      <button
                        type="button"
                        className="kkp-sommaire-list-toggle"
                        onClick={() => setListExpanded((v) => !v)}
                        aria-expanded={listExpanded}
                      >
                        <Icon icon={listExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
                        <span>
                          {listExpanded
                            ? 'Réduire la liste'
                            : `Voir tous (${items.length})`}
                        </span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </aside>

            <section className="kkp-sommaire-editor">
              {!draft ? (
                <div className="kkp-sommaire-empty">
                  Sélectionnez un sommaire à gauche, ou créez-en un nouveau.
                </div>
              ) : (
                <>
                  <div className="kkp-sommaire-editor__head">
                    <div>
                      <p className="kkp-sommaire-editor__title mb-1">{draft.titre}</p>
                      <span className={`kkp-sommaire-badge kkp-sommaire-badge--${draft.statut}`}>
                        {draft.statut === 'validated' ? 'Validé' : 'Brouillon'}
                      </span>
                    </div>
                    <div className="kkp-sommaire-actions">
                      <button type="button" className="btn btn-sm btn-success-600" onClick={handleSave} disabled={saving}>
                        Enregistrer
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleDelete}>
                        Supprimer
                      </button>
                    </div>
                  </div>

                  <div className="kkp-sommaire-field">
                    <label htmlFor="titre">Titre</label>
                    <input
                      id="titre"
                      value={draft.titre || ''}
                      onChange={(e) => setDraft((p) => ({ ...p, titre: e.target.value }))}
                    />
                  </div>

                  <div className="kkp-sommaire-field">
                    <label htmlFor="intro">Introduction du mail</label>
                    <textarea
                      id="intro"
                      value={draft.intro_mail || ''}
                      onChange={(e) => setDraft((p) => ({ ...p, intro_mail: e.target.value }))}
                      placeholder="Bonjour à tous.tes…"
                      style={{ minHeight: 180 }}
                    />
                  </div>

                  <div className="kkp-sommaire-field">
                    <div className="kkp-sommaire-field__label-row">
                      <label htmlFor="texte-brut">Sommaire</label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={openLinkModal}
                        title="Insérer un lien (Ctrl+K)"
                      >
                        <Icon icon="mdi:link-variant" className="me-1" />
                        Insérer un lien
                      </button>
                    </div>
                    <textarea
                      id="texte-brut"
                      ref={texteBrutRef}
                      value={draft.texte_brut || ''}
                      onChange={(e) => setDraft((p) => ({ ...p, texte_brut: e.target.value }))}
                      onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                          e.preventDefault();
                          openLinkModal();
                        }
                      }}
                      placeholder={'Sommaire KKP novembre :\n\nAgenda (Catherine)\n\nPhoto du mois : …\n\nVie locale : …\n\nDossier du mois : …\n- …\n\nVoyager local : …'}
                      style={{ minHeight: 320, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.86rem' }}
                    />
                  </div>

                  <div className="kkp-sommaire-send">
                    <div className="kkp-sommaire-field">
                      <label htmlFor="send-carnet">Carnet d’adresses</label>
                      <select
                        id="send-carnet"
                        value={selectedCarnetId}
                        onChange={(e) => setSelectedCarnetId(e.target.value)}
                      >
                        <option value="">— Aucun carnet —</option>
                        {carnets.filter((c) => c.active !== false).map((carnet) => {
                          const n = carnet.contacts_count || 0;
                          return (
                            <option key={carnet.id} value={carnet.id}>
                              {carnet.name} — {n} contact{n > 1 ? 's' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div className="kkp-sommaire-field">
                      <label htmlFor="extra-emails">Autres e-mails (en plus, séparés par une virgule)</label>
                      <input
                        id="extra-emails"
                        value={extraEmails}
                        onChange={(e) => setExtraEmails(e.target.value)}
                        placeholder="prenom@exemple.fr, autre@exemple.fr"
                      />
                    </div>

                    <button
                      type="button"
                      className="btn kkp-sommaire-primary"
                      onClick={handleValidateSend}
                      disabled={sending}
                    >
                      <Icon icon="mdi:send-check" className="me-1" />
                      {sending ? 'Envoi…' : 'Valider + placer CDF + envoyer'}
                    </button>

                    {(draft.envois || []).length > 0 && (
                      <div className="kkp-sommaire-history">
                        <p className="kkp-sommaire-section-label">Historique des envois</p>
                        <ul>
                          {draft.envois.map((envoi) => (
                            <li key={envoi.id}>
                              {new Date(envoi.sent_at).toLocaleString('fr-FR')}
                              {' — '}
                              {(envoi.recipients || []).join(', ')}
                              {envoi.cdf_placements?.length
                                ? ` · ${envoi.cdf_placements.length} page(s) CDF`
                                : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
            </section>
          </div>
        )}

        {pageTab === 'carnets' && (
          <div className="kkp-sommaire-panel kkp-sommaire-carnets-panel">
            <div className="kkp-sommaire-carnets-panel__head">
              <div>
                <h6 className="mb-1 fw-semibold">Carnets d’adresses</h6>
                <p className="kkp-sommaire-hint mb-0">
                  Choisissez des comptes avec les étiquettes, ou ajoutez des e-mails à la main.
                </p>
              </div>
              <div className="kkp-sommaire-carnet-create kkp-sommaire-carnet-create--inline">
                <input
                  value={newCarnetName}
                  onChange={(e) => setNewCarnetName(e.target.value)}
                  placeholder="Nom du nouveau carnet"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateCarnet();
                    }
                  }}
                />
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleCreateCarnet}>
                  <Icon icon="mdi:plus" className="me-1" />
                  Créer
                </button>
              </div>
            </div>

            {carnets.length === 0 ? (
              <div className="kkp-sommaire-empty">Aucun carnet pour l’instant. Créez-en un ci-dessus.</div>
            ) : (
              <div className="kkp-sommaire-carnets-grid">
                <div className="kkp-sommaire-carnet-list kkp-sommaire-carnet-list--wide">
                  {carnets.map((carnet) => (
                    <button
                      key={carnet.id}
                      type="button"
                      className={`kkp-sommaire-card kkp-sommaire-card--compact${manageCarnetId === carnet.id ? ' kkp-sommaire-card--active' : ''}`}
                      onClick={() => setManageCarnetId(carnet.id)}
                    >
                      <span className="kkp-sommaire-card__title">{carnet.name}</span>
                      <span className="kkp-sommaire-card__meta">
                        {carnet.contacts_count || 0} contact(s)
                      </span>
                    </button>
                  ))}
                </div>

                {manageCarnet && (
                  <div className="kkp-sommaire-carnet-detail kkp-sommaire-carnet-detail--wide">
                    <div className="kkp-sommaire-carnet-detail__head">
                      <div className="kkp-sommaire-carnet-rename">
                        <input
                          value={carnetNameEdit}
                          onChange={(e) => setCarnetNameEdit(e.target.value)}
                          aria-label="Nom du carnet"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleRenameCarnet}
                          disabled={carnetNameEdit.trim() === (manageCarnet.name || '')}
                        >
                          Renommer
                        </button>
                      </div>
                      <button
                        type="button"
                        className="kkp-sommaire-icon-btn"
                        title="Supprimer le carnet"
                        onClick={() => handleDeleteCarnet(manageCarnet.id)}
                      >
                        <Icon icon="mdi:trash-can-outline" />
                      </button>
                    </div>

                    <div className="kkp-sommaire-field mb-3">
                      <label>Personnes (comptes)</label>
                      <PersonPills
                        items={userPillItems}
                        selectedIds={selectedUserIds}
                        onToggle={handleToggleUser}
                        emptyLabel="Aucun compte actif."
                        disabled={contactBusy}
                      />
                    </div>

                    <div className="kkp-sommaire-field mb-2">
                      <label>Ajouter un contact à la main</label>
                      <div className="kkp-sommaire-row" style={{ gridTemplateColumns: '1fr 1.2fr auto', marginBottom: 0 }}>
                        <input
                          value={newContact.name}
                          onChange={(e) => setNewContact((p) => ({ ...p, name: e.target.value }))}
                          placeholder="Nom"
                          disabled={contactBusy}
                        />
                        <input
                          value={newContact.email}
                          onChange={(e) => setNewContact((p) => ({ ...p, email: e.target.value }))}
                          placeholder="e-mail"
                          disabled={contactBusy}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleAddContact}
                          disabled={contactBusy}
                        >
                          Ajouter
                        </button>
                      </div>
                    </div>

                    <p className="kkp-sommaire-section-label mt-3 mb-2">
                      Contacts du carnet ({(manageCarnet.contacts || []).length})
                    </p>
                    {(manageCarnet.contacts || []).length === 0 ? (
                      <p className="kkp-sommaire-hint mb-0">Aucun contact pour l’instant.</p>
                    ) : (
                      <ul className="kkp-sommaire-contact-list kkp-sommaire-contact-list--wide">
                        {(manageCarnet.contacts || []).map((contact) => {
                          const fromUser = users.some(
                            (u) => normalizeEmail(u.email) === normalizeEmail(contact.email),
                          );
                          return (
                            <li key={contact.id}>
                              <span>
                                <strong>{contact.name}</strong>
                                <small>
                                  {contact.email}
                                  {fromUser ? ' · compte' : ' · manuel'}
                                </small>
                              </span>
                              <button
                                type="button"
                                className="kkp-sommaire-icon-btn"
                                onClick={() => handleRemoveContact(contact.id)}
                                title="Retirer"
                                disabled={contactBusy}
                              >
                                <Icon icon="mdi:close" />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      {linkModal && (
        <div
          className="kkp-sommaire-link-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kkp-sommaire-link-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeLinkModal();
          }}
        >
          <form
            className="kkp-sommaire-link-modal__panel"
            onSubmit={confirmLinkModal}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                closeLinkModal();
              }
            }}
          >
            <div className="kkp-sommaire-link-modal__head">
              <h2 id="kkp-sommaire-link-title">
                {linkModal.editing ? 'Modifier le lien' : 'Insérer un lien'}
              </h2>
              <button
                type="button"
                className="kkp-sommaire-icon-btn"
                onClick={closeLinkModal}
                aria-label="Fermer"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
            <div className="kkp-sommaire-field">
              <label htmlFor="kkp-link-text">Texte</label>
              <input
                id="kkp-link-text"
                ref={linkTextRef}
                value={linkModal.text}
                onChange={(e) => setLinkModal((p) => ({ ...p, text: e.target.value }))}
                placeholder="Texte affiché"
                autoComplete="off"
              />
            </div>
            <div className="kkp-sommaire-field mb-0">
              <label htmlFor="kkp-link-url">Lien</label>
              <input
                id="kkp-link-url"
                ref={linkUrlRef}
                value={linkModal.url}
                onChange={(e) => setLinkModal((p) => ({ ...p, url: e.target.value }))}
                placeholder="https://"
                autoComplete="off"
              />
            </div>
            <div className="kkp-sommaire-link-modal__actions">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeLinkModal}>
                Annuler
              </button>
              <button type="submit" className="btn btn-sm btn-primary-600 text-white">
                {linkModal.editing ? 'Mettre à jour' : 'Appliquer'}
              </button>
            </div>
          </form>
        </div>
      )}
      </div>
      {modal}
    </MasterLayout>
  );
};

export default KkpSommairePage;
